import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { sqWorkbookStore } from '@/core/core.stores';
import { WORKBOOK_VERSIONED_ITEM_TYPES } from '@/core/versionHistory.constants';
import { sqAuditApi } from '@/sdk/api/AuditApi';
import type { VersionHistoryOutputV1 } from '@/sdk/model/VersionHistoryOutputV1';

export type VersionHistorySelectOption = {
  label: string;
  value: VersionHistoryOutputV1;
};

export const enum VersionHistoryOperations {
  Update = 'update',
  Create = 'create',
}

/**
 * A hook for managing the state of version history operations in the application.
 *
 * @return an object with the current version history operation, selected version history, new workbook name,
 * functions to set the new workbook name, notes, operation type, selected version history,
 * and a list of writeable version history options.
 */
export const useVersionState = () => {
  const [versionState, setVersionState] = useState({
    operation: VersionHistoryOperations.Create,
    selectedOption: null as VersionHistorySelectOption | null,
    newWorkbookName: sqWorkbookStore.name,
    newWorkbookNameError: null as string | null,
    notes: '',
    versionHistories: [] as VersionHistoryOutputV1[],
    writeableVersionHistories: [] as VersionHistoryOutputV1[],
    writeableVersionHistoryOptions: [] as VersionHistorySelectOption[],
    relatedVersionHistories: [] as VersionHistoryOutputV1[],
    hasFetched: false,
    hasAutoSet: false,
  });

  useEffect(() => {
    const fetchVersionHistories = async () => {
      try {
        const [relatedVersionHistories, allVersionHistories] = await Promise.all([
          sqAuditApi.getVersionHistories({
            duplicateItemId: sqWorkbookStore.workbookId,
            versionedItemType: WORKBOOK_VERSIONED_ITEM_TYPES,
          }),
          sqAuditApi.getVersionHistories({ versionedItemType: WORKBOOK_VERSIONED_ITEM_TYPES }),
        ]);
        const allHistories = allVersionHistories.data.versionHistories;
        const relatedHistories = relatedVersionHistories.data.versionHistories;
        const relatedIds = new Set(relatedHistories.map((history) => history.id));

        // By default, the endpoint returns all version histories with READ or higher permission
        // But for this dropdown, we only want the ones the user can WRITE or ones that belong to the EVERYONE group
        const writeableVersionHistories = allHistories.filter(
          (history) => history.effectivePermissions?.write === true,
        );

        const writeableVersionHistoryOptions: VersionHistorySelectOption[] =
          writeableVersionHistories?.map((item) => ({
            label: `${item.name} ${t('ACCESS_CONTROL.LOCKING.VERSIONING.V')}${item.versions.length + 1}`,
            value: item,
          })) ?? [];

        setVersionState((prev) => ({
          ...prev,
          versionHistories: allHistories,
          writeableVersionHistories: writeableVersionHistories.filter(Boolean) as VersionHistoryOutputV1[],
          writeableVersionHistoryOptions,
          relatedVersionHistories: allHistories.filter((vh) => relatedIds.has(vh.id)),
          hasFetched: true,
        }));
      } catch (error) {
        console.error('Failed to fetch version histories:', error);
      }
    };

    fetchVersionHistories();
  }, []);

  useEffect(() => {
    if (!versionState.hasFetched || versionState.hasAutoSet) return;

    const getNameWithoutVersion = (name: string) =>
      name
        .toLowerCase()
        .replace(/access_control\.locking\.versioning\.v\d+/i, '') // Remove translation key ACCESS_CONTROL.LOCKING.VERSIONING.V1
        .replace(/\s*v\.\d+.*$/, '') // Remove " v.1", " v.2 - Copy", etc.
        .trim();
    const workbookNameBase = getNameWithoutVersion(sqWorkbookStore.name);
    const bestMatch = versionState.writeableVersionHistoryOptions.find(
      (option) => getNameWithoutVersion(option.label) === workbookNameBase,
    );

    if (versionState.relatedVersionHistories.length > 0 && bestMatch) {
      setVersionState((prev) => ({
        ...prev,
        operation: VersionHistoryOperations.Update,
        selectedOption: bestMatch ?? versionState.writeableVersionHistoryOptions[0],
        hasAutoSet: true,
      }));
    } else {
      setVersionState((prev) => ({
        ...prev,
        operation: VersionHistoryOperations.Create,
        hasAutoSet: true,
      }));
    }
  }, [
    versionState.hasFetched,
    versionState.hasAutoSet,
    versionState.relatedVersionHistories.length,
    versionState.writeableVersionHistoryOptions,
  ]);

  const handleSetNewWorkbookName = (name: string) => {
    const isDuplicateName = versionState.versionHistories.some(
      (versionHistory) => versionHistory.name.toLowerCase().trim() === name.toLowerCase().trim(),
    );
    const errorMessage = isDuplicateName ? t('ACCESS_CONTROL.LOCKING.VERSIONING.DUPLICATE_NAME_ERROR') : null;
    setVersionState((prev) => ({ ...prev, newWorkbookName: name, newWorkbookNameError: errorMessage }));
  };

  const handleSetNotes = (notes: string | undefined) => setVersionState((prev) => ({ ...prev, notes: notes ?? '' }));
  const handleSetOperation = (operation: VersionHistoryOperations) =>
    setVersionState((prev) => ({ ...prev, operation }));

  const handleSelectVersionHistory = (option: VersionHistorySelectOption) =>
    setVersionState((prev) => ({ ...prev, selectedOption: option }));

  return {
    versionHistoryOperation: versionState.operation,
    selectedVersionHistory: versionState.selectedOption,
    newWorkbookName: versionState.newWorkbookName,
    newWorkbookNameError: versionState.newWorkbookNameError,
    handleSetNewWorkbookName,
    notes: versionState.notes,
    handleSetNotes,
    handleSetOperation,
    handleSelectVersionHistory,
    versionHistories: versionState.versionHistories,
    writeableVersionHistoryOptions: versionState.writeableVersionHistoryOptions,
  };
};

export type VersionState = ReturnType<typeof useVersionState>;
